// Base API configuration
// In development, if no env is provided, we use relative endpoints (""),
// allowing CRA dev proxy to avoid CORS.
const API_BASE_URL =
  process.env.REACT_APP_API_URL || process.env.REACT_APP_BASE_URL || "";

const ERROR_PAGE_STATUSES = new Set([401, 403, 404, 500, 502, 503]);

function shouldDispatchErrorPage(endpoint, options, status) {
  if (!ERROR_PAGE_STATUSES.has(Number(status))) return false;
  if (options?.suppressErrorPage) return false;
  if (/^\/auth\/(login|logout)$/i.test(String(endpoint || ""))) return false;
  return true;
}

class ApiService {
  async request(endpoint, options = {}) {
    const url = /^https?:\/\//i.test(endpoint)
      ? endpoint
      : `${API_BASE_URL}${endpoint}`;

    const isFormData = options && options.body instanceof FormData;
    const headers = {
      Accept: "application/json",
      ...(isFormData ? {} : { "Content-Type": "application/json" }),
      ...options.headers,
    };

    const config = {
      headers,
      credentials: "include", // Include cookies for HttpOnly authentication
      ...options,
    };

    // `timeout` (ms) is opt-in. fetch has no native timeout, so it only works
    // if wired to an AbortController. Endpoints that stream a keep-alive
    // heartbeat (e.g. BPMN analyze) need a ceiling well above their runtime.
    let timeoutId;
    let timedOut = false;
    if (options.timeout && !config.signal) {
      const controller = new AbortController();
      config.signal = controller.signal;
      timeoutId = setTimeout(() => {
        timedOut = true;
        controller.abort();
      }, options.timeout);
    }

    try {
      const response = await fetch(url, config);

      if (!response.ok) {
        let message = `HTTP error! status: ${response.status}`;
        try {
          const ct = response.headers.get("content-type") || "";
          if (ct.includes("application/json")) {
            const errJson = await response.json();
            if (errJson && typeof errJson === "object") {
              message =
                (Array.isArray(errJson.message) ?
                  errJson.message.join("; ")
                : errJson.message) ||
                errJson.error ||
                JSON.stringify(errJson);
            }
          } else {
            const errText = await response.text();
            if (errText) message = errText;
          }
        } catch (_) {
          // ignore parse errors, keep default message
        }

        const error = new Error(message);
        error.status = response.status;
        error.endpoint = endpoint;

        if (shouldDispatchErrorPage(endpoint, options, response.status)) {
          window.dispatchEvent(
            new CustomEvent("app:http-error", {
              detail: {
                status: response.status,
                message,
                endpoint,
              },
            }),
          );
        }

        throw error;
      }

      // Handle blob/binary responses (e.g., file downloads)
      if (options.responseType === "blob") {
        return await response.blob();
      }

      // Gracefully handle empty bodies (e.g. 204) or non-JSON responses
      const contentType = response.headers.get("content-type") || "";
      if (contentType.includes("application/json")) {
        return await response.json();
      }
      const text = await response.text();
      if (!text) return null;
      try {
        return JSON.parse(text);
      } catch {
        return text;
      }
    } catch (error) {
      if (timedOut) {
        const timeoutError = new Error(
          `Request timed out after ${Math.round(options.timeout / 1000)}s`,
        );
        timeoutError.endpoint = endpoint;
        timeoutError.timeout = true;
        console.error(`API request timed out: ${endpoint}`, timeoutError);
        throw timeoutError;
      }
      console.error(`API request failed: ${endpoint}`, error);
      throw error;
    } finally {
      if (timeoutId) clearTimeout(timeoutId);
    }
  }

  // GET request
  async get(endpoint, options = {}) {
    return this.request(endpoint, { method: "GET", ...options });
  }

  // POST request
  async post(endpoint, data, options = {}) {
    return this.request(endpoint, {
      method: "POST",
      body: JSON.stringify(data),
      ...options,
    });
  }

  // PUT request
  async put(endpoint, data, options = {}) {
    return this.request(endpoint, {
      method: "PUT",
      body: JSON.stringify(data),
      ...options,
    });
  }

  // PATCH request
  async patch(endpoint, data, options = {}) {
    return this.request(endpoint, {
      method: "PATCH",
      body: JSON.stringify(data),
      ...options,
    });
  }

  // DELETE request
  async delete(endpoint) {
    return this.request(endpoint, { method: "DELETE" });
  }
}

const apiInstance = new ApiService();
export default apiInstance;
